import React, { useState, useEffect } from 'react';
import { useAlert } from 'react-alert';
import api from '../utils/axiosInstance';
import {
    AdminShell, FormCard, FormActions, IconSave,
} from './AdminUI';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const config = { headers: { 'Content-Type': 'application/json' }, withCredentials: true };

// ── Eye icon for show/hide toggle ─────────────────────────────────────────────
const IconEyeOpen = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
);
const IconEyeClosed = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" /><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" /><line x1="1" y1="1" x2="23" y2="23" />
    </svg>
);

const AdminEmailSettings = () => {
    const alert = useAlert();

    const [pageLoading, setPageLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Current values from DB
    const [currentEmail, setCurrentEmail] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');

    // New password fields
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    // Show/hide toggles
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    // ── Fetch active email on mount ─────────────────────────────────────────
    useEffect(() => {
        const fetchEmail = async () => {
            setPageLoading(true);
            try {
                const res = await api.get(`${API_BASE_URL}/api/v1/admin/email/active`, config);
                setCurrentEmail(res.data.data.email || '');
                if (res.data.data.passwordNotSet) {
                    setCurrentPassword('Not set yet — use the form below to set it');
                } else {
                    setCurrentPassword(res.data.data.password || '');
                }
            } catch (err) {
                alert.error(err.response?.data?.message || 'Failed to load email settings.');
            } finally {
                setPageLoading(false);
            }
        };
        fetchEmail();
    }, []);

    // ── Submit ──────────────────────────────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!newPassword.trim()) {
            alert.error('Please enter a new password.'); return;
        }
        if (newPassword.length < 8) {
            alert.error('Password must be at least 8 characters.'); return;
        }
        if (newPassword !== confirmPassword) {
            alert.error('Passwords do not match.'); return;
        }

        setSaving(true);
        try {
            await api.put(
                `${API_BASE_URL}/api/v1/admin/email/password/updates`,
                { password: newPassword },
                config
            );
            alert.success('Email password updated successfully!');
            setCurrentPassword(newPassword); // reflect new password in "current" display
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            alert.error(err.response?.data?.message || 'Failed to update password.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <AdminShell
            title="No-Reply Email Settings"
            subtitle="Update the password used to send system emails"
        >
            {pageLoading ? (
                <FormCard>
                    {[1, 2, 3].map(i => (
                        <div key={i} className="sk" style={{ height: 44, marginBottom: 16, borderRadius: 8 }} />
                    ))}
                </FormCard>
            ) : (
                <form onSubmit={handleSubmit} noValidate>

                    {/* ── Current config (read-only) ────────────────────── */}
                    <FormCard title="Current Configuration">

                        <div className="admin-field">
                            <label className="admin-label">Email Address</label>
                            <input
                                className="admin-input"
                                type="text"
                                value={currentEmail}
                                disabled
                                style={{ opacity: 0.6, cursor: 'not-allowed' }}
                            />
                            <p style={{ fontSize: 12, color: '#9ca3af', margin: '5px 0 0' }}>
                                Email address cannot be changed here. Contact your server admin.
                            </p>
                        </div>

                        <div className="admin-field">
                            <label className="admin-label">Current Password</label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    className="admin-input"
                                    type={showCurrent ? 'text' : 'password'}
                                    value={currentPassword}
                                    disabled
                                    style={{ opacity: 0.6, cursor: 'not-allowed', paddingRight: 44 }}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowCurrent(p => !p)}
                                    style={{
                                        position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                                        background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280',
                                        display: 'flex', alignItems: 'center',
                                    }}
                                    title={showCurrent ? 'Hide password' : 'Show password'}
                                >
                                    {showCurrent ? <IconEyeClosed /> : <IconEyeOpen />}
                                </button>
                            </div>
                        </div>
                    </FormCard>

                    {/* ── Update password ────────────────────────────────── */}
                    <FormCard title="Update Password">

                        <div className="admin-field">
                            <label className="admin-label">
                                New Password <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    className="admin-input"
                                    type={showNew ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={e => setNewPassword(e.target.value)}
                                    placeholder="Enter new password"
                                    style={{ paddingRight: 44 }}
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowNew(p => !p)}
                                    style={{
                                        position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                                        background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280',
                                        display: 'flex', alignItems: 'center',
                                    }}
                                >
                                    {showNew ? <IconEyeClosed /> : <IconEyeOpen />}
                                </button>
                            </div>
                            <p style={{ fontSize: 12, color: '#9ca3af', margin: '5px 0 0' }}>
                                Minimum 8 characters.
                            </p>
                        </div>

                        <div className="admin-field">
                            <label className="admin-label">
                                Confirm New Password <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    className="admin-input"
                                    type={showConfirm ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                    placeholder="Re-enter new password"
                                    style={{
                                        paddingRight: 44,
                                        // Live match indicator — red border if typed but doesn't match
                                        borderColor: confirmPassword && confirmPassword !== newPassword ? '#ef4444' : undefined,
                                    }}
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirm(p => !p)}
                                    style={{
                                        position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                                        background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280',
                                        display: 'flex', alignItems: 'center',
                                    }}
                                >
                                    {showConfirm ? <IconEyeClosed /> : <IconEyeOpen />}
                                </button>
                            </div>
                            {/* Live mismatch warning */}
                            {confirmPassword && confirmPassword !== newPassword && (
                                <p style={{ fontSize: 12, color: '#ef4444', fontWeight: 600, margin: '5px 0 0' }}>
                                    ⚠ Passwords do not match.
                                </p>
                            )}
                            {confirmPassword && confirmPassword === newPassword && (
                                <p style={{ fontSize: 12, color: '#16a34a', fontWeight: 600, margin: '5px 0 0' }}>
                                    ✓ Passwords match.
                                </p>
                            )}
                        </div>

                    </FormCard>

                    <FormActions>
                        <button
                            type="submit"
                            disabled={saving || !newPassword || newPassword !== confirmPassword}
                            className="admin-btn admin-btn-success"
                            style={{ minWidth: 180, justifyContent: 'center' }}
                        >
                            <IconSave /> {saving ? 'Updating…' : 'Update Password'}
                        </button>
                    </FormActions>

                </form>
            )}
        </AdminShell>
    );
};

export default AdminEmailSettings;